import * as React from 'react';
import {
    type LayoutRectangle,
    type NativeMethods,
    type StyleProp,
    View,
    type ViewStyle,
} from 'react-native';

import { PortalContext } from '@gitbook/spine-react';
import { useDebouncedFlush, useMergedRefs, useSafeLayoutEffect } from '@gitbook/util-react-hooks';

import { measureNodeInWindow } from '../util';
import { useCurrentPortal } from './context';
import { PortalAbsolute, type PortalAbsoluteRef } from './PortalAbsolute';
import { type CloseableCriterias, useCloseable } from './useCloseable';

export type PortalAnchorProp<T> = (props: { ref: React.Ref<T> }) => React.ReactNode;

// Imperative methods for the Portal ref
export interface PortalRelativeRef extends PortalAbsoluteRef {
    /**
     * Update the position of the portal.
     */
    updatePosition: () => void;
}

export interface PortalRelativeWrapperProps {
    opened: boolean;
    onClick: () => void;
    style: StyleProp<ViewStyle>;
    children: React.ReactNode;
}

export type PortalRelativeProps<T extends NativeMethods> = CloseableCriterias & {
    opened?: boolean;

    /**
     * If true, the portal's position will be updated after the user finished scrolling.
     * @default true
     */
    updateOnScroll?: boolean;

    /**
     * Element to position the portal around.
     */
    anchor: PortalAnchorProp<T>;

    /**
     * Content rendered in the portal.
     */
    children: React.ReactNode;

    /**
     * React reference passed to the anchor element.
     */
    anchorRef?: React.Ref<T | undefined>;

    /**
     * Component used to render the top-level wrapper of the portal,
     * where the position is set.
     */
    wrapper?: React.ComponentType<PortalRelativeWrapperProps>;
};

const PortalRelativePositionContext = React.createContext<LayoutRectangle | null>(null);

/**
 * Return the current position of the relative portal.
 */
export function usePortalRelativePosition(): LayoutRectangle | null {
    const rect = React.useContext(PortalRelativePositionContext);
    return rect;
}

/**
 * Portal that render with the position to help position content around an anchor.
 */
const PortalRelative = React.forwardRef(function Portal<T extends NativeMethods>(
    props: PortalRelativeProps<T>,
    ref: React.Ref<PortalRelativeRef>
): React.ReactElement {
    const anchorRef = React.useRef<T>(null);
    const {
        opened = true,
        updateOnScroll = true,
        anchor,
        wrapper = PortalRelativeDefaultWrapper,
        closeOnClick,
        closeOnEsc,
        closeOnOutsideClick,
        closeOnScroll,
        closeOnClickInNestedPortals,
        children,
    } = props;
    const { close } = useCurrentPortal();
    const portalRef = React.useRef<PortalAbsoluteRef>(null);
    const [position, setPosition] = React.useState<LayoutRectangle | null>(null);

    useCloseable(
        {
            opened,
            closeOnEsc,
            closeOnOutsideClick,
            closeOnScroll,
            closeOnClick,
            closeOnClickInNestedPortals,
            onClose: close,
        },
        portalRef,
        anchorRef
    );

    const getContainer = () => {
        const portal = portalRef.current;

        if (!portal) {
            return null;
        }

        return portal.getContainer();
    };

    const updatePosition = React.useCallback(() => {
        const anchorNode = anchorRef.current;

        if (!anchorNode) {
            return;
        }

        // @ts-ignore anchorNode is an HTMLElement in RNW
        const rect = measureNodeInWindow(anchorNode);
        setPosition({
            ...rect,
            y: rect.y,
        });
    }, [anchorRef]);

    // Update the position when opening
    useSafeLayoutEffect(() => {
        if (opened) {
            updatePosition();
        }
    }, [opened, updatePosition]);

    // Once position has been updated, ensure container is visible
    // if previously hidden by scroll
    useSafeLayoutEffect(() => {
        const container = getContainer();

        if (!container) {
            return;
        }

        // Ensure the element is visible since the position has been updated
        if (container.style.display === 'none' && position) {
            container.style.display = 'block';
        }
    }, [opened, position]);

    // Hide the portal when scrolling
    // and update its position when we are done.
    const debounceScroll = useDebouncedFlush(250);
    const getPortalContainer = React.useContext(PortalContext);
    React.useEffect(() => {
        const container = getContainer();
        if (!opened || !container || !updateOnScroll) {
            return;
        }

        let hidden = false;
        const onScroll = (event: Event) => {
            const portalsRoot = getPortalContainer();
            if (portalsRoot.contains(event.target as Node)) {
                // Ignore scroll in a portal
                return;
            }

            // Hide it
            if (!hidden) {
                container.style.display = 'none';
                hidden = true;
            }

            // Shot it and update position once we are done scrolling
            debounceScroll(() => {
                hidden = false;
                updatePosition();
            });
        };

        window.addEventListener('scroll', onScroll, true);
        return () => {
            debounceScroll.cancel();
            window.removeEventListener('scroll', onScroll, true);
        };
    }, [opened, updateOnScroll, updatePosition, getPortalContainer]);

    React.useImperativeHandle(
        ref,
        (): PortalRelativeRef => ({
            getContainer,
            updatePosition: opened ? updatePosition : () => {},
        })
    );

    const mergedRef = useMergedRefs(props.anchorRef, anchorRef);

    return (
        <>
            {anchor({ ref: mergedRef })}

            <PortalAbsolute ref={portalRef}>
                {position ? (
                    <PortalRelativePositionContext.Provider value={position}>
                        {React.createElement(
                            wrapper,
                            {
                                opened,
                                // @ts-ignore
                                onClick: (event) => {
                                    event.stopPropagation();
                                },
                                // @ts-ignore 'fixed' is accepted as a value
                                style: {
                                    position: 'fixed',
                                    ...(position
                                        ? {
                                              top: position.y,
                                              left: position.x,
                                              width: position.width,
                                              height: position.height,
                                          }
                                        : {}),
                                },
                            },
                            children
                        )}
                    </PortalRelativePositionContext.Provider>
                ) : null}
            </PortalAbsolute>
        </>
    );
}) as <T extends NativeMethods>(
    p: PortalRelativeProps<T> & { ref?: React.Ref<PortalRelativeRef> }
) => React.ReactElement;

function PortalRelativeDefaultWrapper(
    props: PortalRelativeWrapperProps
): React.ReactElement | null {
    const { opened, ...rest } = props;

    return opened ? <View {...rest} /> : null;
}

// @ts-ignore
PortalRelative.displayName = 'PortalRelative';

export { PortalRelative };
